import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '~/utils/misc.tsx'
import { Ripple } from '../../ripple/Ripple.tsx'

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-full text-sm font-bold ring-offset-cards transition-colors transition-shadow  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-primary text-secondary hover:bg-primary-900 ',
        secondary: 'bg-secondary text-primary hover:bg-secondary-700 ',
        cancel: 'bg-gray-700 text-white hover:bg-gray-700/90',
        outline:
          'border border-input bg-white hover:border-foreground focus:border-2 focus:border-foreground focus-visible:ring-0',
        'outline-primary':
          ' border border-primary text-primary bg-white hover:bg-background hover:shadow-buttons focus:border-2 focus:border-primary focus-visible:ring-0',
        'outline-secondary':
          ' border border-secondary text-secondary-700 bg-white hover:bg-background hover:shadow-buttons focus:border-2 focus:border-primary focus-visible:ring-0',
        // secondary: 'bg-secondary text-white hover:bg-secondary/80',
        ghost: 'hover:bg-gray-100 text-gray-600',
        danger: 'bg-danger text-white hover:bg-badge-red-foreground ',
        white: 'bg-white text-black hover:bg-gray-300',
        gray: 'bg-primary-100 hover:bg-primary-200 text-primary-600',
        overlay: 'bg-black bg-opacity-30 hover:bg-opacity-100 text-white',
        // link: 'text-primary-text underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-11 px-4 py-2',
        wide: 'px-24 py-5',
        'xs-circle': 'h-7 rounded-full px-1.5',
        circle: 'h-11 rounded-full w-11',
        xs: 'h-7 px-1.5',
        sm: 'h-9 rounded-md px-3',
        lg: 'h-12 rounded-md px-8',
        pill: 'px-12 py-3 leading-3',
        icon: 'h-11 w-11',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  enableRipple?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, enableRipple, children, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(
          'relative overflow-hidden',
          buttonVariants({ variant, size, className }),
        )}
        ref={ref}
        {...props}
      >
        {children}
        {enableRipple && <Ripple />}
      </button>
    )
  },
)
Button.displayName = 'Button'

export interface ButtonTriggerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof buttonVariants> {
  disableRiple?: boolean
}

const ButtonTrigger = React.forwardRef<HTMLDivElement, ButtonTriggerProps>(
  ({ className, disableRiple, children, variant, size, ...props }, ref) => {
    return (
      <div
        className={cn(
          'relative cursor-pointer overflow-hidden',
          buttonVariants({ variant, size, className }),
        )}
        ref={ref}
        {...props}
      >
        {children}
        {!disableRiple && <Ripple />}
      </div>
    )
  },
)
ButtonTrigger.displayName = 'ButtonTrigger'

export { Button, ButtonTrigger, buttonVariants }
